import { z } from 'zod'
import type { CreateAnnouncementDto } from '../dto/announcements/request/create-announcement.dto'
import {
  AnnouncementPriority,
  AnnouncementTargetRole,
  AnnouncementTargetType,
  AnnouncementVisibility,
} from '../enums/announcement.enums'

const uuidString = z.string().uuid({ message: 'Identifiant invalide' })

/**
 * Schéma de validation pour la création d'annonce (formulaire)
 */
export const announcementFormSchema = z
  .object({
    title: z
      .string()
      .min(3, 'Le titre doit contenir au moins 3 caractères.')
      .max(200, 'Le titre ne peut pas dépasser 200 caractères.'),
    content: z
      .string()
      .min(3, 'Le contenu doit contenir au moins 3 caractères.'),
    priority: z.nativeEnum(AnnouncementPriority),
    targetType: z.nativeEnum(AnnouncementTargetType),
    targetRole: z
      .union([z.nativeEnum(AnnouncementTargetRole), z.literal('')])
      .optional(),
    establishmentId: z
      .union([uuidString, z.literal('')])
      .optional(),
    classId: z.union([uuidString, z.literal('')]).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.targetType === AnnouncementTargetType.ROLE) {
      if (!data.targetRole) {
        ctx.addIssue({
          code: 'custom',
          path: ['targetRole'],
          message: 'Veuillez sélectionner un rôle cible pour cette annonce.',
        })
      }
    }
    if (data.targetType === AnnouncementTargetType.CLASS) {
      if (!data.establishmentId || data.establishmentId === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['establishmentId'],
          message: 'Veuillez sélectionner un établissement.',
        })
      }
      if (!data.classId || data.classId === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['classId'],
          message: 'Veuillez sélectionner une classe pour cette annonce.',
        })
      }
    }
  })

export type AnnouncementFormData = z.infer<typeof announcementFormSchema>

/**
 * Construit le DTO API à partir des données formulaire validées.
 * @param scopeEstablishmentId — établissement courant (scope) pour les modes ALL / ROLE
 */
export function mapAnnouncementFormToCreateDto(
  data: AnnouncementFormData,
  scopeEstablishmentId?: string
): CreateAnnouncementDto {
  const establishmentIdForScope =
    scopeEstablishmentId && scopeEstablishmentId !== ''
      ? scopeEstablishmentId
      : undefined

  const base: CreateAnnouncementDto = {
    title: data.title.trim(),
    content: data.content.trim(),
    priority: data.priority,
    visibility: AnnouncementVisibility.CUSTOM,
    publishedAt: new Date().toISOString(),
  }

  switch (data.targetType) {
    case AnnouncementTargetType.ALL:
      return {
        ...base,
        establishmentId: establishmentIdForScope,
        targetRoles: undefined,
      }
    case AnnouncementTargetType.ROLE:
      return {
        ...base,
        establishmentId: establishmentIdForScope,
        targetRoles: [data.targetRole as AnnouncementTargetRole],
      }
    case AnnouncementTargetType.CLASS:
      return {
        ...base,
        establishmentId: data.establishmentId as string,
        classId: data.classId as string,
      }
    default:
      return {
        ...base,
        establishmentId: establishmentIdForScope,
      }
  }
}
